/**
 * For now we disable the import sorting rule here since if not then it makes
 * the `@root/css/web.less` to be moved at the end making final stylings to
 * change their graph order causing some components/views to have their styles
 * losing against global definitions.
 */
// eslint-disable-next-line simple-import-sort/imports
import 'cssVars';
import '@root/css/web.less';
import EventBus from 'components/event-bus';
import AppRouterView, { configureRouter } from 'components/router';
import analytics from 'controllers/analytics';
import i18next from 'i18next';
import i18nextIntervalPlural from 'i18next-intervalplural-postprocessor';
import I18NextVue from 'i18next-vue';
import { installAjaxDialogHandlers } from 'ops/ajax-dialogs';
import {
	configureBugsnag,
	launch,
} from 'ops/launch';
import {
	AppStateModule,
	configureStore,
	ProductStateModule,
} from 'store';
import { viewportTools } from 'tools';
import getLocale from 'tools/get-locale';
import importCustomLanguage from 'tools/import-custom-language';
import {
	FALLBACK_LANGUAGES,
	resolveBackgroundLanguages,
	resolveBootLanguages,
} from 'tools/translation-languages';
import setNativeLanguage from 'tools/set-native-language';
import {
	configureChildren,
	configureCurrentInstance,
	configureDevTools,
	configureDialogService,
	configureDynamicParent,
	configureFlagPrototype,
	configureForceCompute,
	configureGetSlot,
	configureIsMounted,
	configureRenderComponent,
	configureTheme,
	configureToolbarService,
	configureTooltipService,
	configureUnmount,
} from 'utils/vue';
import elementDoubleTap from 'utils/element-double-tap';
import reportError from 'utils/report-error';
import { createApp } from 'vue';

type TranslationBundle = Record<string, unknown>;

// Create application
export default class App {
	public vueRouter: InstanceType<typeof AppRouterView> | undefined;

	public standalone = false;

	private defaultLanguageNamespace = 'translation';

	private languageImports = new Map<string, Promise<void>>();

	private vueHMRReloadPatched = false;

	private vueHMRReloadRetryTimeout?: NodeJS.Timeout;

	private vueHMRReloadRetryCount = 0;

	constructor() {
		// Running as standalone app?
		this.standalone = window.navigator.userAgent.indexOf('FBAV') !== -1
			|| window.navigator.userAgent.indexOf('Instagram') !== -1
			|| (('standalone' in window.navigator) && window.navigator.standalone)
			|| window.matchMedia('(display-mode: standalone)').matches;

		// Set locale
		window.locale = getLocale();

		// Add translation strings for native interface
		if (window.glPlatform === 'native') {
			setNativeLanguage(window.locale);

			if (!window.webToNative) {
				throw new Error('Missing WebToNative on window');
			}

			/*
			 * Store the native device details in a globally accessible window parameter
			 * This way we don't have to call the promise everytime we need access to these
			 * static details
			 */
			window.webToNative
				.getNativeDetails()
				.then((nativeDeviceDetails) => {
					window.nativeDeviceDetails = nativeDeviceDetails;
				})
				.catch((e) => {
					reportError(
						e,
						'warning',
					);
				});
		}
	}

	get router() {
		if (!this.vueRouter) {
			throw new Error('Router is not available yet. Did you call init on this class?');
		}

		return this.vueRouter;
	}

	init(): Promise<InstanceType<typeof AppRouterView>> {
		if (this.vueRouter) {
			return Promise.resolve(this.vueRouter);
		}

		i18next.on(
			'missingKey',
			(_lngs, ns, key) => {
				if (ns === this.defaultLanguageNamespace) {
					const e = new Error(
						`Could not find translation value for key ${key}`,
					);

					if (typeof window !== 'undefined' && typeof window.glBugsnagClient !== 'undefined') {
						reportError(
							e,
							'warning',
						);
					} else {
						throw e;
					}
				}
			},
		);

		return i18next
			.use(i18nextIntervalPlural)
			.init({
				defaultNS: this.defaultLanguageNamespace,
				debug: window.glStack === 'local',
				lng: window.locale,
				fallbackLng: FALLBACK_LANGUAGES,
				supportedLngs: window.glLanguages,
				saveMissing: true,
				/**
				 * Force namespace parsing even when keys contain spaces (e.g., font ids like "Hepta Slab").
				 */
				nsSeparator: ':',
			})
			.then(() => (
				/*
				 * Only the active locale and the `en` fallback are needed to render;
				 * the remaining bundles load in the background after launch
				 * (see importBackgroundLanguages).
				 */
				Promise.all(resolveBootLanguages(window.locale).map((ln) => this.importLanguage(ln)))
			))
			.then(() => {
				const app = createApp(
					AppRouterView,
					{
						theme: 'light',
					},
				);
				configureBugsnag(app);
				configureStore(app);
				configureRouter(app);
				configureCurrentInstance(app);
				configureDevTools(app);
				configureDynamicParent(app);
				configureChildren(app);
				configureUnmount(app);
				configureIsMounted(app);
				configureRenderComponent(app);
				configureFlagPrototype(app);
				configureGetSlot(app);
				configureForceCompute(app);
				configureDialogService(app);
				installAjaxDialogHandlers();
				configureTheme(app);
				configureToolbarService(app);
				configureTooltipService(app);
				elementDoubleTap.setup();
				viewportTools.configureAppHeight();
				app.use(
					I18NextVue,
					{
						i18next,
					},
				);

				this.vueRouter = app.mount('#webapp') as InstanceType<typeof AppRouterView>;
				this.configureVueHMRReload();

				return this.vueRouter;
			});
	}

	/**
	 * Patch Vue's HMR reload and rerender methods to also trigger a reload on the router,
	 * ensuring that route components are properly reloaded when their code changes.
	 * This is a temporary workaround since currently the Vue's HMR seems to be broken not
	 * reflecting changes in the DOM forcing developers to manually refresh the page after each change.
	 */
	private configureVueHMRReload() {
		if (
			process.env.NODE_ENV === 'production'
			|| this.vueHMRReloadPatched
		) {
			return;
		}

		const hmrRuntime = window.__VUE_HMR_RUNTIME__;

		if (
			!hmrRuntime
			&& this.vueHMRReloadRetryCount < 10
		) {
			this.vueHMRReloadRetryCount += 1;
			this.scheduleVueHMRReloadPatch();
			return;
		}
		if (!hmrRuntime) {
			console.warn('[HMR] Could not find Vue HMR runtime to patch reload/rerender methods');
			return;
		}

		const originalRerender = hmrRuntime.rerender;
		const originalReload = hmrRuntime.reload;

		hmrRuntime.rerender = (
			id,
			render,
		) => {
			originalRerender(
				id,
				render,
			);

			console.log(`[HMR] Performing Vue HMR rerender for component with id ${id}`);
			this.vueRouter?.reload();
		};

		hmrRuntime.reload = (
			id,
			component,
		) => {
			originalReload(
				id,
				component,
			);

			if (!this.shouldReloadCurrentRouteComponent(component)) {
				return;
			}

			console.log(`[HMR] Performing Vue HMR reload for component with file ${component.__file}`);
			this.vueRouter?.reload();
		};

		this.vueHMRReloadPatched = true;
	}

	private scheduleVueHMRReloadPatch() {
		if (
			this.vueHMRReloadPatched
			|| typeof this.vueHMRReloadRetryTimeout !== 'undefined'
		) {
			return;
		}

		this.vueHMRReloadRetryTimeout = setTimeout(
			() => {
				this.vueHMRReloadRetryTimeout = undefined;
				this.configureVueHMRReload();
			},
			50,
		);
	}

	private shouldReloadCurrentRouteComponent(component: Parameters<NonNullable<typeof window.__VUE_HMR_RUNTIME__>['reload']>[1]) {
		return !!(
			this.vueRouter
			&& component.__file?.includes('.vue')
		);
	}

	start() {
		// Add event handlers for document events
		document.addEventListener(
			'mousemove',
			(e) => {
				EventBus.emit(
					'mouse:move',
					e,
				);
			},
		);
		document.addEventListener(
			'touchmove',
			(e) => {
				EventBus.emit(
					'mouse:move',
					e,
				);
			},
			{
				passive: false,
			},
		);

		document.addEventListener(
			'mousedown',
			(e) => {
				EventBus.emit(
					'mouse:down',
					e,
				);
			},
		);
		document.addEventListener(
			'touchstart',
			(e) => {
				EventBus.emit(
					'mouse:down',
					e,
				);
			},
		);

		document.addEventListener(
			'mouseup',
			(e) => {
				EventBus.emit(
					'mouse:up',
					e,
				);
			},
		);
		document.addEventListener(
			'touchend',
			(e) => {
				EventBus.emit(
					'mouse:up',
					e,
				);
			},
		);

		if (window.glPlatform === 'native') {
			if (!window.nativeToWeb) {
				throw new Error('Missing native bridge');
			}

			window.nativeToWeb.onApplicationResume = (payload) => {
				if (
					payload
					&& 'unreadMessagesCount' in payload
				) {
					AppStateModule.setUnreadMessageCount(
						payload.unreadMessagesCount || 0,
					);
				}

				EventBus.emit('app:resume');
			};
		} else if (window.glPlatform === 'web') {
			document.addEventListener(
				'visibilitychange',
				() => {
					if (document.visibilityState !== 'hidden') {
						EventBus.emit('app:resume');
					}
				},
			);

			window.addEventListener(
				'beforeinstallprompt',
				(e) => {
					// Prevent Chrome 67 and earlier from automatically showing the prompt
					e.preventDefault();

					// Stash the event so it can be triggered later.
					window.deferredPrompt = e;

					/*
					 * TODO: Fix the event type
					 */
					// @ts-expect-error: `userChoice` is not typed on the event but it's available
					e.userChoice.then((choiceResult) => {
						analytics.trackEvent(
							'Install prompt',
							{
								category: 'Banner',
								label: 'Action',
								value: choiceResult.outcome,
							},
						);
					});
				},
			);

			window.addEventListener(
				'beforeunload',
				(e) => {
					if (!ProductStateModule.getSaved) {
						(e || window.event).returnValue = 'Not all changes have been saved yet.';
						return 'Not all changes have been saved yet.';
					}
					return null;
				},
			);
		}

		this
			.init()
			.then(() => {
				// Launch app
				launch()
					.then(() => {
						this.importBackgroundLanguages();
					})
					.catch(() => {
						// launch() surfaces its own failures; nothing to do here
					});
			});
	}

	/**
	 * Load the locale bundles that were skipped at boot once the launch chain
	 * has completed, so runtime language switching keeps working without having
	 * blocked the initial render on every bundle.
	 */
	private importBackgroundLanguages(): void {
		resolveBackgroundLanguages(window.locale).forEach((ln) => {
			this.importLanguage(ln);
		});
	}

	/**
	 * Make sure every bundle on the i18next lookup chain for the given locale
	 * is loaded. Callers switching the runtime language must await this before
	 * `i18next.changeLanguage`, because the background bundles may still be
	 * loading shortly after launch.
	 */
	ensureLanguageLoaded(locale: string): Promise<void> {
		return Promise
			.all(resolveBootLanguages(locale).map((ln) => this.importLanguage(ln)))
			.then(() => undefined);
	}

	importLanguage(ln: string): Promise<void> {
		const pendingImport = this.languageImports.get(ln);

		if (pendingImport) {
			return pendingImport;
		}

		const languageImport = import(
			/* @vite-ignore */
			/* webpackChunkName: "translations" */
			`./i18n/${ln}/default.json`
		)
			.then((defaultModule) => {
				const defaultTranslation = (defaultModule as {
					default?: TranslationBundle;
				}).default ?? {};
				i18next.addResourceBundle(
					ln,
					this.defaultLanguageNamespace,
					defaultTranslation,
				);

				return importCustomLanguage(ln);
			})
			.then((labelModule) => {
				const labelTranslation = (labelModule as {
					default?: TranslationBundle;
				}).default ?? {};
				i18next.addResourceBundle(
					ln,
					this.defaultLanguageNamespace,
					labelTranslation,
					true,
					true,
				);
			})
			.catch((e) => {
				/*
				 * Drop the failed import from the cache so a later call (e.g. a
				 * runtime language switch) can retry, rather than reusing a cached
				 * promise that resolved to a no-op failure.
				 */
				this.languageImports.delete(ln);

				// No translation file available
				reportError(
					e,
					'warning',
				);
			});

		this.languageImports.set(
			ln,
			languageImport,
		);

		return languageImport;
	}
}
